import React, { useEffect, useState, Fragment, useRef } from 'react'
import Grid from '@mui/material/Grid2'
import { StandardPage } from '../_EOS/StandardPage/StandardPage'
import { Filter, FilterComponent } from '../_WMS/FilterComponent'
import { deletePosition, exportPositionsMassive, getPositionDetail, getPositionList } from './api'
import { PositionDetailInfoDto, PositionListInfoDto } from '../../interfaces/Position/Position'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { Status } from '../../hooks/useFetchState'
import { usePagination } from '../../hooks/usePagination'
import { GenericTableV2 } from '../_EOS/GenericTable/GenericTableV2'
import { filterOptions, tableColumns } from './config'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../_EOS/Modal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../SnackBar/Snackbar'
import { CreatePosition, CreatePositionHandle } from './CreatePosition/CreatePosition'
import { ControlsUpload, ControlsUploadHandle } from './PositionMassive/ControlsUpload'
import { PositionDetail } from './PositionDetail/PositionDetail'
import { Avatar, Box, ListItemAvatar, Typography } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import EditIcon from '@mui/icons-material/Edit'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'
import { DialogInfo } from './components/DialogInfo'

export const PositionPageNew: React.FC = () => {
  const [selectedFilters, setSelectedFilters] = React.useState<Filter[]>([])
  const { state, callApi, setIdle } = useApi<ApiResponse<PositionListInfoDto>>()
  const { callApi: callDetail, state: detailState } = useApi<PositionDetailInfoDto>()
  const { callApi: callDelete, state: deleteState } = useApi()
  const { callApi: callExport, state: exportState } = useApi()
  const exportModal = useModal('')
  const pagination = usePagination(5)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const viewModal = useModal<{ item: PositionDetailInfoDto }>('')
  const deleteModal = useModal<{ positionId: number; positionName: string }>('')

  const [orderBy, setOrderBy] = useState<{ field: keyof PositionListInfoDto; direction: 'asc' | 'desc' }>({
    field: 'positionName',
    direction: 'asc'
  })

  const handleSorting = (field: keyof PositionListInfoDto, direction: 'asc' | 'desc') => {
    const newOrder = { field, direction }
    setOrderBy(newOrder)
    requestPositions(selectedFilters, pagination.page, pagination.pageSize, newOrder)
  }

  const positionRef = useRef<CreatePositionHandle>(null)
  const uploadRef = useRef<ControlsUploadHandle>(null)
  const [createEditOpen, setCreateEditOpen] = useState(false)
  const [editableItem, setEditableItem] = useState<PositionDetailInfoDto | undefined>(undefined)
  const [massiveOpen, setMassiveOpen] = useState(false)
  const [massiveEditMode, setMassiveEditMode] = useState(false)
  const [uploadDisabled, setUploadDisabled] = useState(true)

  const requestPositions = (filters?: Filter[], page?: number, pageSize?: number, order?: { field: keyof PositionListInfoDto; direction: 'asc' | 'desc' }) => {
    callApi(getPositionList(filters, page, pageSize, order ?? orderBy)).catch((error) => {
      console.log(error)
      openSnackBar(error, false)
    })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestPositions(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleDelete = () => {
    const positionId = deleteModal.extendedProps?.positionId
    if (!positionId) return

    callDelete(deletePosition(positionId))
      .then(() => {
        openSnackBar('Posición eliminada correctamente.', true)
        deleteModal.closeModal()
        requestPositions(selectedFilters, pagination.page, pagination.pageSize)
      })
      .catch((error) => {
        openSnackBar(error, false)
        deleteModal.closeModal()
      })
  }

  const handleOpenCreate = () => {
    setEditableItem(undefined)
    setCreateEditOpen(true)
  }

  const handleOpenEdit = (row: PositionListInfoDto) => {
    callDetail(getPositionDetail(row.positionId))
      .then((detail) => {
        setEditableItem(detail as PositionDetailInfoDto)
        setCreateEditOpen(true)
      })
      .catch((error) => {
        console.log(error)
        openSnackBar(error || 'Error al obtener el detalle de la posición.', false)
      })
  }

  const handleOpenView = (row: PositionListInfoDto) => {
    callDetail(getPositionDetail(row.positionId))
      .then((detail) => viewModal.openModal('md', { item: detail as PositionDetailInfoDto }))
      .catch((error) => {
        console.log(error)
        openSnackBar(error || 'Error al obtener el detalle de la posición.', false)
      })
  }

  const handleCloseCreateEdit = () => {
    setCreateEditOpen(false)
    setEditableItem(undefined)
    requestPositions(selectedFilters, pagination.page, pagination.pageSize)
  }

  // Handler separado para el cierre por éxito real (mensaje de string), distinto del cierre
  // "en seco" (onClose/cancelButton), que reciben el evento de UI en vez de un mensaje.
  const handleCreateEditSuccess = (message: string) => {
    handleCloseCreateEdit()
    openSnackBar(message, true)
  }

  const handleFetchExport = () => {
    callExport(exportPositionsMassive(selectedFilters))
      .then(() => {
        exportModal.closeModal()
        openSnackBar('Proceso de exportación agendado.', true)
      })
      .catch((error) => {
        exportModal.closeModal()
        openSnackBar(error, false)
      })
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    requestPositions(selectedFilters, 0, pagination.pageSize)
  }, [orderBy])

  const selectedItem = viewModal.extendedProps?.item

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={(filters) => {
            setSelectedFilters(filters)
            setIdle()
          }}
          onSearchClick={requestPositions}
          includeSearchOnClean={true}
          includeAddButton={true}
          onAddButtonClick={handleOpenCreate}
          extraActions={[
            {
              label: 'Crear posiciones masivas',
              icon: <AddCircleIcon fontSize='small' />,
              onClick: () => { setMassiveEditMode(false); setUploadDisabled(true); setMassiveOpen(true) }
            },
            {
              label: 'Editar posiciones masivas',
              icon: <EditIcon fontSize='small' />,
              onClick: () => { setMassiveEditMode(true); setUploadDisabled(true); setMassiveOpen(true) }
            },
            {
              label: 'Descargar posiciones',
              icon: <FileDownloadIcon fontSize='small' />,
              onClick: () => exportModal.openModal('md'),
              disabled: !state.data?.items?.length
            }
          ]}
        />

        <GenericTableV2<PositionListInfoDto>
          columns={tableColumns}
          keyField='positionId'
          data={state.data?.items || []}
          loading={state.status === Status.Loading || detailState.status === Status.Loading}
          pagination={{
            total: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize,
            onPageChange: handlePaginationChange
          }}
          sorting={{
            field: orderBy.field,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              icon: <EditIcon />,
              onClick: (row) => handleOpenEdit(row),
              disabled: (row) => !row.canUpdatePosition || row.isExternalStorage
            },
            {
              key: 'view',
              label: 'Ver Detalle',
              icon: <VisibilityIcon />,
              onClick: (row) => handleOpenView(row)
            },
            {
              key: 'delete',
              label: 'Eliminar',
              icon: <DeleteIcon />,
              onClick: (row) => deleteModal.openModal('sm', { positionId: row.positionId, positionName: row.positionName }),
              disabled: (row) => !row.canUpdatePosition
            }
          ]}
        />
      </StandardPage>

      {/* Modal Crear / Editar */}
      <Modal
        open={createEditOpen}
        onClose={handleCloseCreateEdit}
        maxWidth='lg'
        title={editableItem ? 'Editar Posición' : 'Crear Posición'}
        successButton={{ action: () => positionRef.current?.submitForm(), title: 'Guardar' }}
        cancelButton={{ action: handleCloseCreateEdit, title: 'Cerrar' }}
      >
        <Box sx={{ pb: 0, mb: -2 }}>
          <CreatePosition
            ref={positionRef}
            editableItem={editableItem}
            onSuccess={handleCreateEditSuccess}
          />
        </Box>
      </Modal>

      {/* Modal Crear / Editar Masivo */}
      <Modal
        open={massiveOpen}
        onClose={() => setMassiveOpen(false)}
        maxWidth='lg'
        title={massiveEditMode ? 'Editar Posiciones Masivas' : 'Crear Posiciones Masivas'}
        successButton={{ action: () => uploadRef.current?.upload(), title: 'Cargar', disabled: uploadDisabled }}
        cancelButton={{ action: () => setMassiveOpen(false), title: 'Cerrar' }}
      >
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <DialogInfo editMode={massiveEditMode} />
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>
            <ControlsUpload
              ref={uploadRef}
              editMode={massiveEditMode}
              onValidationChange={setUploadDisabled}
              onClose={(success, msg) => {
                setMassiveOpen(false)
                if (msg) openSnackBar(msg, success)
              }}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Modal Detalle */}
      <Modal
        open={viewModal.isOpen}
        onClose={viewModal.closeModal}
        maxWidth='md'
        title='Detalle de Posición'
        cancelButton={{ action: viewModal.closeModal, title: 'Cerrar' }}
      >
        {selectedItem && <PositionDetail item={selectedItem} />}
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        open={deleteModal.isOpen}
        onClose={deleteModal.closeModal}
        maxWidth={deleteModal.size}
        title='Confirmar eliminación'
        loading={deleteState.status === Status.Loading}
        successButton={{ action: handleDelete, title: 'Confirmar' }}
        cancelButton={{ action: deleteModal.closeModal, title: 'Cancelar' }}
      >
        <Typography variant='h6'>¿Desea eliminar la posición <b>{deleteModal.extendedProps?.positionName}</b>?</Typography>
      </Modal>

      {/* Modal Confirmar Exportación */}
      <Modal
        open={exportModal.isOpen}
        onClose={exportModal.closeModal}
        maxWidth='md'
        title='¿Desea continuar con la exportación?'
        loading={exportState.status === Status.Loading}
        successButton={{ action: handleFetchExport, title: 'Aceptar' }}
        cancelButton={{ action: exportModal.closeModal, title: 'Cancelar' }}
      >
        <Box display='flex' alignItems='flex-start'>
          <ListItemAvatar>
            <Avatar>
              <WarningIcon />
            </Avatar>
          </ListItemAvatar>
          <Box>
            <Typography variant='body1'>
              La generación del archivo se realizará en segundo plano. Una vez solicitada, la exportación estará disponible en el menú de procesos.
            </Typography>
            <Typography variant='body1' color='textSecondary' style={{ marginTop: 8 }}>
              Si desea limitar los resultados, aplique los filtros antes de continuar. Este proceso puede tardar unos minutos.
            </Typography>
          </Box>
        </Box>
      </Modal>

      <SnackBar
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
        message={message}
      />
    </Fragment>
  )
}
